import * as tf from '@tensorflow/tfjs'

let audioContext = null

const FRAMES = 172
const FREQ_BINS = 232
const TARGET_LENGTH = FRAMES * FREQ_BINS // 39,904 — same as live pipeline
// BROWSER_FFT uses analyser.fftSize = config.fftSize*2 = 2048, hop = config.fftSize = 1024 samples.
// Using the same values here ensures the 232 frequency bins cover 0–5 kHz (21.5 Hz/bin),
// identical to what the live microphone pipeline produces.
const FFT_WINDOW = 2048

export const processAudioFile = async (
    file,
    { stateRef, add, setCapturedSamples, updateReduxClassState }
) => {
    try {
        const ctx =
            audioContext ||
            (audioContext = new (window.AudioContext ||
                window.webkitAudioContext)())
        const buffer = await ctx.decodeAudioData(await file.arrayBuffer())
        const channelData = buffer.getChannelData(0)
        const sampleRate = buffer.sampleRate

        // BROWSER_FFT hop = fftSize/sampleRateHz samples = 1024 at 44.1 kHz.
        // Scale proportionally for files recorded at other sample rates.
        const hopLength = Math.round(sampleRate * 1024 / 44100) // ~1024 at 44.1 kHz

        // Chunk must produce at least FRAMES frames:
        // numFrames = floor((chunkSize - FFT_WINDOW) / hopLength) + 1
        // Using chunkSize = FRAMES * hopLength + FFT_WINDOW gives numFrames = FRAMES + 1 (sliced below).
        const chunkSize = FRAMES * hopLength + FFT_WINDOW // ~4 seconds at 44.1 kHz
        const newSamples = []

        // Dynamic noise floor: measure RMS of first 100 ms
        const initialSegment = channelData.slice(0, Math.floor(sampleRate * 0.1))
        const noiseFloor = Math.sqrt(
            initialSegment.reduce((s, v) => s + v * v, 0) /
                (initialSegment.length || 1)
        )
        const dynamicThreshold = Math.max(0.01, noiseFloor * 1.5)

        for (
            let offset = 0;
            offset < channelData.length;
            offset += chunkSize
        ) {
            const chunk = channelData.slice(offset, offset + chunkSize)
            if (chunk.length < chunkSize) continue

            const rms = Math.sqrt(
                chunk.reduce((s, v) => s + v * v, 0) / chunk.length
            )
            if (rms < dynamicThreshold) continue

            // ── Bug #2A+B fix ────────────────────────────────────────────────
            // tf.tidy disposes all intermediate tensors automatically (Bug #4).
            // Produces a flat Float32Array of exactly TARGET_LENGTH (39,904) values.
            const spectrogramTensor = tf.tidy(() => {
                const t = tf.tensor1d(chunk)

                // stft shape: [numFrames, FFT_WINDOW/2 + 1] = [~173, 1025]
                // hopLength ≈ 1024 matches BROWSER_FFT's frame interval exactly.
                const stft = tf.signal.stft(t, FFT_WINDOW, hopLength)
                const mag = tf.abs(stft)

                // Match Web Audio API getFloatFrequencyData dBFS formula:
                //   dBFS = 20*log10(|X[k]|) − 20*log10(FFT_WINDOW/2)
                // Without subtracting the normalisation offset (60.2 dB for window=2048),
                // typical speech magnitudes land at +30…+50 dB and are all clipped to 0,
                // making the spectrogram a flat wall that the model cannot distinguish.
                const DB_NORM = 20 * Math.log10(FFT_WINDOW / 2) // 60.2 dB for window=2048
                const db = mag.add(1e-6).log().mul(20 / Math.LN10).sub(DB_NORM)
                const clipped = db.clipByValue(-100, 0)
                // clipped.shape = [numFrames, 1025]

                const numFrames = clipped.shape[0]

                // Slice or pad to exactly [FRAMES, FREQ_BINS]
                if (numFrames >= FRAMES) {
                    return clipped
                        .slice([0, 0], [FRAMES, FREQ_BINS])
                        .reshape([TARGET_LENGTH])
                }

                // Pad missing frames with silence (−100 dB)
                const sliced = clipped.slice([0, 0], [numFrames, FREQ_BINS])
                const padded = sliced.pad(
                    [[0, FRAMES - numFrames], [0, 0]],
                    -100
                )
                return padded.reshape([TARGET_LENGTH])
            })

            // Extract JS array outside tidy; then release the output tensor.
            const flatData = await spectrogramTensor.array()
            spectrogramTensor.dispose()

            // ── Bug #2C fix ──────────────────────────────────────────────────
            // detectHumanVoice() calls recognizer.wordLabels() and iterates the
            // score array by label index — passing a 3-element fake array means
            // only the first 3 labels (always _background_noise_ / _unknown_)
            // are checked, so speechProbability is always 0.
            // Instead: use a direct heuristic on the real spectrogram data.
            //
            // Human speech energy is concentrated in bins 12–186 (≈ 250–4000 Hz
            // at 44.1 kHz / 2048-point FFT, where each bin ≈ 21.5 Hz).
            let isSpeechLikely = false
            if (rms > 0.05) {
                const midFrameOffset = Math.floor(FRAMES / 2) * FREQ_BINS
                const speechEnergy = flatData
                    .slice(midFrameOffset + 12, midFrameOffset + 187)
                    .reduce((a, b) => a + (b + 100), 0)
                const highEnergy = flatData
                    .slice(midFrameOffset + 187, midFrameOffset + FREQ_BINS)
                    .reduce((a, b) => a + (b + 100), 0)
                isSpeechLikely = speechEnergy > highEnergy * 0.7
            }

            const sample = {
                modelId: stateRef.current.currentModelId,
                classId: stateRef.current.className,
                sampleType: 'Audio',
                timestamp: Date.now(),
                isHumanCandidate: isSpeechLikely,
                isHumanFromTF: isSpeechLikely,
                sampleData: {
                    // flatData is 39,904 values — matches recorded sample shape exactly
                    data: flatData,
                    frameSize: FREQ_BINS,
                },
            }

            const id = await add(sample)
            newSamples.push({ ...sample, id })
        }

        // ── Bug #4 fix: single setCapturedSamples call after all chunks ──────
        // Previous pattern called setCapturedSamples inside the loop (once per
        // 1-second chunk), triggering a full React re-render + canvas repaint
        // for every second of uploaded audio. Batching to one call here avoids
        // O(n_chunks × n_samples) re-renders.
        const combined = [...stateRef.current.capturedSamples, ...newSamples]
        setCapturedSamples(combined)

        const humanCount = combined.filter(
            (s) => s.isHumanCandidate || s.isHumanFromTF
        ).length
        const categoryName =
            combined.length > 0 && humanCount > combined.length * 0.3
                ? 'Human'
                : 'Non Human'

        updateReduxClassState(combined, true, categoryName)
    } catch (error) {
        console.error('Error processing audio:', error)
    }
}
